import React, { useEffect, useRef, useState } from 'react';
import { Brain, FileText, FolderPlus, MessageSquare } from 'lucide-react';

const steps = [
  {
    icon: FolderPlus,
    title: 'Create Your Space',
    description:
      'Start by creating a project - a dedicated workspace for organizing all your work in one place -' +
      ' and invite your team as needed.',
  },
  {
    icon: MessageSquare,
    title: 'Choose Your Format',
    description:
      'Pick the perfect format - AI chat, notes, lists, tasks, moodboards or bookmarks. Each designed for' +
      ' its' +
      ' specific purpose.',
  },
  {
    icon: Brain,
    title: 'Work Naturally',
    description:
      'Use text or voice while our AI maintains context and learns from your interactions to provide better assistance.',
  },
  {
    icon: FileText,
    title: 'Gain perspective',
    description:
      'All your content - documents, tasks, notes, and visuals - stays organized and connected within your project.',
  },
];

export function HowItWorks() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  
  // Monitor visibility for main animation trigger - works when scrolling in both directions
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Update visibility state based on current intersection
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.disconnect();
      }
    };
  }, []);
  
  // Track scroll position for parallax effect
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      
      const rect = sectionRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const sectionTop = rect.top;
      const sectionHeight = rect.height;
      
      // Calculate scroll progress relative to this section
      // 0 when section top is at bottom of viewport, 1 when section bottom is at top of viewport
      const progress = 1 - (sectionTop + sectionHeight) / (viewportHeight + sectionHeight);
      setScrollProgress(Math.min(Math.max(progress, 0), 1));
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initialize on mount
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Get style for title - parallax effect
  const getTitleStyle = () => ({
    transform: `translateY(${scrollProgress * -50}px)`,
    opacity: Math.min(1, Math.max(0, 1 - scrollProgress * 1.5))
  });
  
  // Get style for each step - staggered slide-in when scrolled into view
  const getStepStyle = (index: number) => {
    const baseDelay = 150;
    return {
      transform: isVisible 
        ? `translateY(0) scale(1)` 
        : `translateY(50px) scale(0.95)`,
      opacity: isVisible ? 1 : 0,
      transition: `transform 0.8s ease-out, opacity 0.8s ease-out`,
      transitionDelay: isVisible ? `${index * baseDelay}ms` : '0ms'
    };
  };

  // Simplified icon style without rotation
  const getIconStyle = () => ({
    transition: 'transform 0.3s ease-out'
  });

  return (
    <div ref={sectionRef} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-40">
      <h2 
        className="text-4xl font-bold text-white text-center mb-20 font-['Sora'] transition-all duration-500"
        style={getTitleStyle()}
      >
        How It Works
      </h2>

      <div className="relative">
        {/* Remove the thin blue connecting line */}
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 relative">
          {steps.map((step, index) => (
            <div
              key={step.title}
              className="text-center relative"
              style={getStepStyle(index)}
            >
              <div className="mb-8 relative">
                <div className="w-20 h-20 rounded-2xl bg-blue-600/10 flex items-center justify-center mx-auto transition-all duration-500 hover:shadow-glow">
                  <step.icon 
                    size={40} 
                    className="text-blue-500 transition-all duration-300"
                    style={getIconStyle()}
                  />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4 transition-colors duration-300 hover:text-blue-400">
                {step.title}
              </h3>
              <p className="text-gray-400 text-lg leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
